import type { FlowRow } from 'constants/payloads/realtime';

/**
 * Pure merge helpers for the realtime flow store (spec §11.3, D23). Extracted from
 * useOptionFlowRealtime so the tricky incremental paths are unit-testable in isolation.
 *
 * `seen` is a caller-owned Set<seq> mutated in lock-step with the returned array — it lets a
 * delta dedupe in O(1) without re-scanning the held session. Both helpers keep the array sorted
 * newest-first by seq and capped to `maxRows` (newest kept); final display order is imposed
 * downstream by sortOptionFlowTrades, so the array only needs "newest kept", not a stable sort.
 */

/**
 * Build the initial sorted+capped set from a snapshot (or any full replacement) and reset
 * `seen` to exactly the seqs we keep (newest `maxRows` by seq).
 */
export function dedupeSortCap(incoming: FlowRow[], seen: Set<number>, maxRows: number): FlowRow[] {
  seen.clear();
  const bySeq = new Map<number, FlowRow>();
  for (const r of incoming) bySeq.set(r.seq, r); // idempotent re-delivery: last wins
  const sorted = Array.from(bySeq.values()).sort((a, b) => b.seq - a.seq);
  const capped = sorted.length > maxRows ? sorted.slice(0, maxRows) : sorted;
  for (const r of capped) seen.add(r.seq);
  return capped;
}

/**
 * Incremental delta apply. Avoids the old per-flush full Map-rebuild + full re-sort of the
 * ENTIRE session — the expensive path at ~44k rows twice a second. New rows are deduped via
 * `seen`; in the common case where every new seq is newer than the current head they are simply
 * prepended (no full sort). A rare out-of-order row (free-tier delayed ASK release) falls back to
 * a one-off merge sort. Returns `prev` unchanged (same ref) when nothing is new.
 */
export function appendRows(
  prev: FlowRow[],
  batch: FlowRow[],
  seen: Set<number>,
  maxRows: number,
): FlowRow[] {
  const fresh: FlowRow[] = [];
  for (const r of batch) {
    if (!seen.has(r.seq)) {
      seen.add(r.seq);
      fresh.push(r);
    }
  }
  if (!fresh.length) return prev;
  fresh.sort((a, b) => b.seq - a.seq);
  const headSeq = prev.length ? prev[0].seq : -Infinity;
  let next =
    fresh[fresh.length - 1].seq > headSeq
      ? fresh.concat(prev) // fast path: all newer than head → prepend, no full sort
      : fresh.concat(prev).sort((a, b) => b.seq - a.seq);
  if (next.length > maxRows) {
    for (let i = maxRows; i < next.length; i++) seen.delete(next[i].seq);
    next = next.slice(0, maxRows);
  }
  return next;
}
